import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Building2, MapPin, Search, Loader2, Shield, Check, Layers } from 'lucide-react';
import { clientAccessService, clientSubRegionsService } from '../services';
import { getApiError } from '@/lib/utils';

const ManageClientAccessModal = ({ isOpen, onClose, userId, userName, userEmail, onAccessUpdated }) => {
  const { toast } = useToast();
  const [groups, setGroups] = useState([]);
  const [subRegions, setSubRegions] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [selectedSubRegions, setSelectedSubRegions] = useState(new Set());
  const [selectedSites, setSelectedSites] = useState(new Set());
  const [initialGroupIds, setInitialGroupIds] = useState([]);
  const [initialSubRegionIds, setInitialSubRegionIds] = useState([]);
  const [initialSiteIds, setInitialSiteIds] = useState([]);
  const [siteSearch, setSiteSearch] = useState('');
  const [activeTab, setActiveTab] = useState('groups');
  const [effectiveSiteCount, setEffectiveSiteCount] = useState(0);

  const loadData = useCallback(async () => {
    if (!isOpen || !userId) return;
    setLoading(true);
    try {
      const [groupsData, sitesData, accessData] = await Promise.all([
        clientAccessService.listGroups(),
        clientAccessService.listSites({ limit: 1000 }),
        clientAccessService.getAccess(userId)
      ]);
      setGroups(groupsData);
      setSites(sitesData);

      const assignedGroupIds = accessData.assigned_groups.map(g => g.id);
      const assignedSubRegionIds = (accessData.assigned_sub_regions || []).map(sr => sr.id);
      const assignedSiteIds = accessData.assigned_sites.map(s => s.id);
      setSelectedGroups(new Set(assignedGroupIds));
      setSelectedSubRegions(new Set(assignedSubRegionIds));
      setSelectedSites(new Set(assignedSiteIds));
      setInitialGroupIds(assignedGroupIds);
      setInitialSubRegionIds(assignedSubRegionIds);
      setInitialSiteIds(assignedSiteIds);

      // Load sub-regions for all groups in parallel
      const subRegionPromises = groupsData.map(g =>
        clientSubRegionsService.list(g.id).then(srData =>
          (srData.items || []).map(sr => ({ ...sr, group_name: g.name, group_id: g.id }))
        ).catch(() => [])
      );
      const allSubRegionArrays = await Promise.all(subRegionPromises);
      setSubRegions(allSubRegionArrays.flat());
    } catch (error) {
      toast({ title: 'Error', description: getApiError(error, 'Failed to load'), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [isOpen, userId, toast]);

  useEffect(() => { loadData(); }, [loadData]);

  // Recalculate effective site count
  useEffect(() => {
    if (loading) return;
    const sitesFromGroups = new Set();
    selectedGroups.forEach(groupId => {
      sites.filter(s => s.group_id === groupId).forEach(s => sitesFromGroups.add(s.id));
    });
    const sitesFromSubRegions = new Set();
    selectedSubRegions.forEach(srId => {
      const sr = subRegions.find(r => r.id === srId);
      if (sr?.sites) sr.sites.forEach(s => sitesFromSubRegions.add(s.id));
    });
    const allSites = new Set([...sitesFromGroups, ...sitesFromSubRegions, ...selectedSites]);
    setEffectiveSiteCount(allSites.size);
  }, [selectedGroups, selectedSubRegions, selectedSites, sites, subRegions, loading]);

  const toggleGroup = (id) => setSelectedGroups(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSubRegion = (id) => setSelectedSubRegions(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSite = (id) => setSelectedSites(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await clientAccessService.updateAccess(
        userId,
        Array.from(selectedGroups),
        Array.from(selectedSubRegions),
        Array.from(selectedSites)
      );
      toast({ title: 'Access Updated', description: `${userName} now has access to ${result.effective_site_count} sites.` });
      onAccessUpdated?.(result);
      onClose();
    } catch (error) {
      toast({ title: 'Error', description: getApiError(error, 'Failed to update access'), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const hasChanges = () => {
    return JSON.stringify([...selectedGroups].sort()) !== JSON.stringify([...initialGroupIds].sort()) ||
      JSON.stringify([...selectedSubRegions].sort()) !== JSON.stringify([...initialSubRegionIds].sort()) ||
      JSON.stringify([...selectedSites].sort()) !== JSON.stringify([...initialSiteIds].sort());
  };

  const filteredSites = sites.filter(s =>
    !siteSearch || s.name.toLowerCase().includes(siteSearch.toLowerCase()) ||
    (s.group_name && s.group_name.toLowerCase().includes(siteSearch.toLowerCase()))
  );

  const hasSubRegions = subRegions.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Manage Client Access
          </DialogTitle>
          <DialogDescription>
            Configure access for <strong>{userName}</strong> ({userEmail}).
            Effective access is the union of all assigned groups, sub-regions, and sites.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">Effective Site Access:</span>
                </div>
                <Badge variant="secondary" className="bg-blue-100 text-blue-800">{effectiveSiteCount} sites</Badge>
              </div>
              <p className="text-xs text-blue-700 mt-1">
                {selectedGroups.size} group(s) {selectedSubRegions.size > 0 && `\u2022 ${selectedSubRegions.size} sub-region(s) `}\u2022 {selectedSites.size} direct site(s)
              </p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className={`grid w-full ${hasSubRegions ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <TabsTrigger value="groups" className="flex items-center gap-1.5 text-xs">
                  <Building2 className="h-3.5 w-3.5" />
                  Groups ({selectedGroups.size})
                </TabsTrigger>
                {hasSubRegions && (
                  <TabsTrigger value="sub-regions" className="flex items-center gap-1.5 text-xs">
                    <Layers className="h-3.5 w-3.5" />
                    Sub-Regions ({selectedSubRegions.size})
                  </TabsTrigger>
                )}
                <TabsTrigger value="sites" className="flex items-center gap-1.5 text-xs">
                  <MapPin className="h-3.5 w-3.5" />
                  Sites ({selectedSites.size})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="groups" className="mt-4">
                <ScrollArea className="h-[300px] pr-4">
                  {groups.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No groups available</p>
                  ) : (
                    <div className="space-y-2">
                      {groups.map(group => (
                        <label key={group.id}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${selectedGroups.has(group.id) ? 'bg-primary/5 border-primary' : 'bg-background hover:bg-muted/50'}`}
                          data-testid={`group-${group.id}`}>
                          <div className="flex items-center gap-3">
                            <Checkbox checked={selectedGroups.has(group.id)} onCheckedChange={() => toggleGroup(group.id)} />
                            <div>
                              <p className="font-medium text-sm">{group.name}</p>
                              <p className="text-xs text-muted-foreground">{group.site_count} sites</p>
                            </div>
                          </div>
                          {selectedGroups.has(group.id) && <Check className="h-4 w-4 text-primary" />}
                        </label>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              {hasSubRegions && (
                <TabsContent value="sub-regions" className="mt-4">
                  <p className="text-xs text-muted-foreground mb-3">
                    Sub-regions are subsets of sites within a group. Selecting a sub-region grants access to all its sites.
                  </p>
                  <ScrollArea className="h-[280px] pr-4">
                    <div className="space-y-2">
                      {subRegions.map(sr => (
                        <label key={sr.id}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${selectedSubRegions.has(sr.id) ? 'bg-violet-50 border-violet-400' : 'bg-background hover:bg-muted/50'}`}
                          data-testid={`subregion-${sr.id}`}>
                          <div className="flex items-center gap-3">
                            <Checkbox checked={selectedSubRegions.has(sr.id)} onCheckedChange={() => toggleSubRegion(sr.id)} />
                            <div>
                              <p className="font-medium text-sm">{sr.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {sr.group_name} \u2022 {sr.site_count} sites
                              </p>
                            </div>
                          </div>
                          {selectedSubRegions.has(sr.id) && <Check className="h-4 w-4 text-violet-600" />}
                        </label>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>
              )}

              <TabsContent value="sites" className="mt-4">
                <div className="mb-3 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search sites..." value={siteSearch} onChange={e => setSiteSearch(e.target.value)} className="pl-9" data-testid="site-search" />
                </div>
                <ScrollArea className="h-[260px] pr-4">
                  {filteredSites.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">{siteSearch ? 'No matching sites' : 'No sites available'}</p>
                  ) : (
                    <div className="space-y-2">
                      {filteredSites.map(site => {
                        const isGroupSelected = site.group_id && selectedGroups.has(site.group_id);
                        const isSubRegionSelected = subRegions.some(sr => selectedSubRegions.has(sr.id) && sr.sites?.some(s => s.id === site.id));
                        const implicitAccess = isGroupSelected || isSubRegionSelected;
                        return (
                          <label key={site.id}
                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedSites.has(site.id) ? 'bg-primary/5 border-primary' :
                              implicitAccess ? 'bg-emerald-50 border-emerald-200' : 'bg-background hover:bg-muted/50'
                            }`} data-testid={`site-${site.id}`}>
                            <div className="flex items-center gap-3">
                              <Checkbox checked={selectedSites.has(site.id)} onCheckedChange={() => toggleSite(site.id)} />
                              <div>
                                <p className="font-medium text-sm">{site.name}</p>
                                {site.group_name && (
                                  <p className="text-xs text-muted-foreground">
                                    {site.group_name}
                                    {isGroupSelected && <span className="ml-1 text-emerald-600">(via group)</span>}
                                    {isSubRegionSelected && !isGroupSelected && <span className="ml-1 text-violet-600">(via sub-region)</span>}
                                  </p>
                                )}
                              </div>
                            </div>
                            {(selectedSites.has(site.id) || implicitAccess) && (
                              <Check className={`h-4 w-4 ${implicitAccess ? 'text-emerald-600' : 'text-primary'}`} />
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading || !hasChanges()} data-testid="save-access-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save Access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManageClientAccessModal;
